import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { readChapterApi, likeChapterApi, getCommentsApi, postCommentApi, likeCommentApi, deleteCommentApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const S = process.env.REACT_APP_API_URL?.replace('/api','') || '';

// ─────────────────────────────────────────────────────────────
// Comment thread component
// ─────────────────────────────────────────────────────────────
function CommentItem({ comment, bookId, chapterId, currentUser, onDelete, onLike, onReplyPosted }) {
  const [showReply, setShowReply]   = useState(false);
  const [replyText, setReplyText]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localLiked, setLocalLiked] = useState(
    currentUser ? comment.likes?.some(id => (id._id||id) === currentUser._id) : false
  );
  const [localLikeCount, setLocalLikeCount] = useState(comment.likes?.length || 0);

  const avatar = comment.user?.name?.[0]?.toUpperCase() || '?';
  const isOwn  = currentUser && comment.user?._id === currentUser._id;
  const isMine = currentUser && (comment.user?._id === currentUser._id || currentUser.role === 'admin');
  const timeAgo = (date) => {
    const d = Math.floor((Date.now() - new Date(date)) / 1000);
    if (d < 60) return 'just now';
    if (d < 3600) return `${Math.floor(d/60)}m ago`;
    if (d < 86400) return `${Math.floor(d/3600)}h ago`;
    return `${Math.floor(d/86400)}d ago`;
  };

  const doLike = async () => {
    if (!currentUser) return;
    setLocalLiked(l => !l);
    setLocalLikeCount(c => localLiked ? c-1 : c+1);
    try {
      await likeCommentApi(comment._id);
    } catch { setLocalLiked(l => !l); setLocalLikeCount(c => localLiked ? c+1 : c-1); }
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await postCommentApi({ chapterId, bookId, text: replyText, parentCommentId: comment._id });
      onReplyPosted(comment._id, data);
      setReplyText(''); setShowReply(false);
    } catch(e) { alert(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
        {/* Avatar */}
        <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#e8c547,#e53170)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'0.85rem', flexShrink:0, color:'#0f0e17' }}>
          {avatar}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <strong style={{ color:'var(--text)', fontSize:'0.88rem' }}>{comment.user?.name || 'User'}</strong>
            {isOwn && <span style={{ fontSize:'0.7rem', color:'var(--gold)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>You</span>}
            <span style={{ color:'var(--text3)', fontSize:'0.78rem' }}>{timeAgo(comment.createdAt)}</span>
          </div>
          <p style={{ color:'var(--text2)', fontSize:'0.9rem', lineHeight:1.65, marginBottom:10 }}>{comment.text}</p>

          {/* Actions */}
          <div style={{ display:'flex', gap:16, alignItems:'center' }}>
            <button onClick={doLike} style={{ background:'none', border:'none', cursor: currentUser?'pointer':'default', display:'flex', alignItems:'center', gap:5, color: localLiked ? 'var(--accent)' : 'var(--text3)', fontSize:'0.8rem', fontFamily:'var(--font-b)', transition:'color 0.2s' }}>
              <span style={{ fontSize:'1rem' }}>{localLiked ? '♥' : '♡'}</span>
              {localLikeCount > 0 && <span>{localLikeCount}</span>}
            </button>
            {currentUser && (
              <button onClick={() => setShowReply(r=>!r)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:'0.8rem', fontFamily:'var(--font-b)', transition:'color 0.2s' }}>
                ↩ Reply
              </button>
            )}
            {isMine && (
              <button onClick={() => onDelete(comment._id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--accent)', fontSize:'0.78rem', fontFamily:'var(--font-b)', opacity:0.7 }}>
                Delete
              </button>
            )}
          </div>

          {/* Reply box */}
          {showReply && (
            <div style={{ marginTop:12, display:'flex', gap:8 }}>
              <textarea
                value={replyText}
                onChange={e=>setReplyText(e.target.value)}
                placeholder="Write a reply…"
                rows={2}
                style={{ flex:1, padding:'8px 12px', background:'var(--bg3)', border:'1.5px solid var(--border2)', borderRadius:8, color:'var(--text)', fontFamily:'var(--font-b)', fontSize:'0.85rem', resize:'none', outline:'none' }}
              />
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <button className="btn btn-gold btn-sm" onClick={submitReply} disabled={submitting || !replyText.trim()}>
                  {submitting ? '…' : 'Post'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setShowReply(false);setReplyText('');}}>Cancel</button>
              </div>
            </div>
          )}

          {/* Replies */}
          {comment.replies?.length > 0 && (
            <div style={{ marginTop:14, paddingLeft:16, borderLeft:'2px solid var(--border2)', display:'flex', flexDirection:'column', gap:14 }}>
              {comment.replies.map(r => (
                <CommentItem key={r._id} comment={r} bookId={bookId} chapterId={chapterId}
                  currentUser={currentUser} onDelete={onDelete} onLike={onLike} onReplyPosted={onReplyPosted} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main ReadChapter component
// ─────────────────────────────────────────────────────────────
export default function ReadChapter() {
  const { bookId, chapterId } = useParams();
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [chapter,  setChapter]  = useState(null);
  const [locked,   setLocked]   = useState(false);
  const [preview,  setPreview]  = useState('');
  const [loading,  setLoading]  = useState(true);

  // Reading UX
  const [fontSize,   setFontSize]   = useState(19);
  const [lineHeight, setLineHeight] = useState(1.95);
  const [theme,      setTheme]      = useState('dark'); // 'dark' | 'sepia' | 'light'
  const [progress,   setProgress]   = useState(0);
  const contentRef = useRef(null);

  // Like state
  const [liked,      setLiked]      = useState(false);
  const [likeCount,  setLikeCount]  = useState(0);
  const [likePending, setLikePending] = useState(false);

  // Comments
  const [comments,    setComments]    = useState([]);
  const [commTotal,   setCommTotal]   = useState(0);
  const [commLoading, setCommLoading] = useState(false);
  const [newComment,  setNewComment]  = useState('');
  const [posting,     setPosting]     = useState(false);
  const [showComments, setShowComments] = useState(false);

  // ── Load chapter ──────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setLocked(false);
    readChapterApi(bookId, chapterId)
      .then(({data}) => {
        setChapter(data);
        setLiked(data.userLiked || false);
        setLikeCount(data.likeCount || 0);
        window.scrollTo({top:0, behavior:'smooth'});
      })
      .catch(err => {
        if (err.response?.data?.locked) {
          setLocked(true);
          setPreview(err.response.data.preview || '');
        } else navigate(`/books/${bookId}`);
      })
      .finally(() => setLoading(false));
  }, [bookId, chapterId, navigate]);

  // ── Load comments ─────────────────────────────────────────
  const loadComments = useCallback(() => {
    if (!chapterId) return;
    setCommLoading(true);
    getCommentsApi(chapterId)
      .then(({data}) => { setComments(data.comments); setCommTotal(data.total); })
      .catch(console.error)
      .finally(() => setCommLoading(false));
  }, [chapterId]);

  useEffect(() => { if (showComments) loadComments(); }, [showComments, loadComments]);

  // ── Scroll progress ───────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight;
      const scrolled = Math.max(0, -rect.top);
      setProgress(Math.min(100, Math.round((scrolled / (total - window.innerHeight)) * 100)));
    };
    window.addEventListener('scroll', handler, { passive:true });
    return () => window.removeEventListener('scroll', handler);
  }, [chapter]);

  // ── Like ──────────────────────────────────────────────────
  const toggleLike = async () => {
    if (!user) return navigate('/login');
    if (likePending) return;
    setLikePending(true);
    const wasLiked = liked;
    setLiked(l=>!l);
    setLikeCount(c => wasLiked ? c-1 : c+1);
    try { await likeChapterApi(chapterId); }
    catch { setLiked(wasLiked); setLikeCount(c => wasLiked ? c+1 : c-1); }
    finally { setLikePending(false); }
  };

  // ── Post comment ──────────────────────────────────────────
  const submitComment = async () => {
    if (!newComment.trim() || posting) return;
    setPosting(true);
    try {
      const { data } = await postCommentApi({ chapterId, bookId, text: newComment });
      setComments(c => [data, ...c]);
      setCommTotal(t => t+1);
      setNewComment('');
    } catch(e) { alert(e.response?.data?.message || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const deleteComment = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteCommentApi(id);
      setComments(c => c.filter(cm => cm._id !== id && !cm.replies?.some(r=>r._id===id))
        .map(cm => ({...cm, replies:(cm.replies||[]).filter(r=>r._id!==id)})));
      setCommTotal(t => t-1);
    } catch { alert('Could not delete'); }
  };

  const handleReplyPosted = (parentId, reply) => {
    setComments(c => c.map(cm =>
      cm._id === parentId ? {...cm, replies:[...(cm.replies||[]), reply]} : cm
    ));
  };

  // ── Theme styles ──────────────────────────────────────────
  const themes = {
    dark:  { bg:'#0f0e17', surface:'#1a1930', text:'#d4d3e8', label:'Dark' },
    sepia: { bg:'#f4ede4', surface:'#ede0d0', text:'#433422', label:'Sepia' },
    light: { bg:'#ffffff', surface:'#f5f5f5', text:'#1a1a2e', label:'Light' },
  };
  const T = themes[theme];

  // ─────────────────────────────────────────────────────────
  if (loading) return <div className="spinner" style={{marginTop:120}} />;

  if (locked) return (
    <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 16px' }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'48px 40px', maxWidth:560, width:'100%', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:20 }}>
        <div style={{ fontSize:'3rem' }}>🔒</div>
        <h2 style={{ fontFamily:'var(--font-d)', fontSize:'2rem', color:'var(--text)' }}>Chapter Locked</h2>
        <p style={{ color:'var(--text2)' }}>Purchase the full book to continue reading.</p>
        <div style={{ position:'relative', width:'100%', background:'var(--bg3)', borderRadius:10, padding:'20px', textAlign:'left', maxHeight:140, overflow:'hidden' }}>
          <p style={{ fontFamily:'var(--font-d)', fontSize:'1rem', lineHeight:1.75, color:'var(--text2)' }}>{preview}</p>
          <div style={{ position:'absolute', bottom:0, left:0, right:0, height:72, background:`linear-gradient(transparent, var(--surface))` }} />
        </div>
        <Link to={`/books/${bookId}`} className="btn btn-gold btn-lg">Unlock Full Book</Link>
      </div>
    </div>
  );

  if (!chapter) return null;

  const paragraphs = chapter.content.split('\n').filter(p=>p.trim());

  return (
    <div style={{ background: T.bg, minHeight:'100vh', transition:'background 0.3s' }}>
      {/* ── Reading progress bar ──────────────────────────── */}
      <div style={{ position:'fixed', top:0, left:0, right:0, height:3, background:'rgba(255,255,255,0.08)', zIndex:300 }}>
        <div style={{ height:'100%', width:`${progress}%`, background:'linear-gradient(90deg,var(--gold),var(--amber))', transition:'width 0.1s' }} />
      </div>

      {/* ── Sticky reading toolbar ────────────────────────── */}
      <div style={{ position:'sticky', top:0, background: theme==='dark' ? 'rgba(15,14,23,0.95)' : theme==='sepia' ? 'rgba(244,237,228,0.95)' : 'rgba(255,255,255,0.95)', backdropFilter:'blur(12px)', borderBottom:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.1)'}`, zIndex:100, padding:'10px 0' }}>
        <div className="container" style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          <Link to={`/books/${bookId}`} style={{ color:'var(--text3)', fontWeight:700, fontSize:'0.78rem', letterSpacing:'0.06em', textTransform:'uppercase', display:'flex', alignItems:'center', gap:6, textDecoration:'none' }}>
            ← Back
          </Link>

          <div style={{ flex:1, textAlign:'center' }}>
            <span style={{ color: T.text, fontSize:'0.82rem', opacity:0.6, fontWeight:700 }}>
              Chapter {chapter.chapterNumber} — {progress}% read
            </span>
          </div>

          {/* Font size */}
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <button onClick={()=>setFontSize(f=>Math.max(14,f-1))} style={{ background:'var(--bg3)', border:'none', width:28, height:28, borderRadius:6, cursor:'pointer', color:'var(--text2)', fontWeight:900, fontFamily:'var(--font-b)' }}>A-</button>
            <span style={{ color:'var(--text3)', fontSize:'0.78rem', minWidth:28, textAlign:'center' }}>{fontSize}</span>
            <button onClick={()=>setFontSize(f=>Math.min(28,f+1))} style={{ background:'var(--bg3)', border:'none', width:28, height:28, borderRadius:6, cursor:'pointer', color:'var(--text2)', fontWeight:900, fontFamily:'var(--font-b)' }}>A+</button>
          </div>

          {/* Theme switcher */}
          <div style={{ display:'flex', gap:4 }}>
            {Object.entries(themes).map(([k,t])=>(
              <button key={k} onClick={()=>setTheme(k)} style={{ width:24, height:24, borderRadius:'50%', background:t.bg, border:`2px solid ${theme===k?'var(--gold)':'var(--border2)'}`, cursor:'pointer', transition:'border-color 0.2s' }} title={t.label} />
            ))}
          </div>

          {/* Line height */}
          <select value={lineHeight} onChange={e=>setLineHeight(+e.target.value)} style={{ background:'var(--bg3)', border:'1px solid var(--border2)', color:'var(--text2)', borderRadius:6, padding:'4px 8px', fontSize:'0.78rem', fontFamily:'var(--font-b)' }}>
            <option value={1.6}>Compact</option>
            <option value={1.95}>Normal</option>
            <option value={2.3}>Spacious</option>
          </select>
        </div>
      </div>

      {/* ── Chapter content ───────────────────────────────── */}
      <div ref={contentRef} style={{ maxWidth:680, margin:'0 auto', padding:'52px 24px 20px' }}>
        {/* Chapter header */}
        <div style={{ marginBottom:40, textAlign:'center' }}>
          <p style={{ color:'var(--gold)', fontWeight:700, fontSize:'0.78rem', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:14 }}>
            Chapter {chapter.chapterNumber}
          </p>
          <h1 style={{ fontFamily:'var(--font-d)', fontSize:'clamp(1.8rem,4vw,2.6rem)', fontWeight:600, color:T.text, lineHeight:1.25, marginBottom:20 }}>
            {chapter.title}
          </h1>
          <div style={{ display:'flex', justifyContent:'center', gap:20, color: theme==='dark'?'var(--text3)':'rgba(0,0,0,0.4)', fontSize:'0.82rem' }}>
            <span>📖 {chapter.wordCount?.toLocaleString()||0} words</span>
            <span>👁 {chapter.readCount?.toLocaleString()||0} reads</span>
            <span>♥ {likeCount} likes</span>
          </div>
          <div style={{ width:60, height:2, background:'var(--gold)', margin:'28px auto 0', opacity:0.5 }} />
        </div>

        {/* Body text */}
        <div style={{ fontFamily:'var(--font-d)', fontSize:fontSize, lineHeight, color:T.text, transition:'all 0.3s' }}>
          {paragraphs.map((p,i) => (
            <p key={i} style={{ marginBottom:'1.6em', textIndent: i===0?0:'2em' }}>{p}</p>
          ))}
        </div>

        {/* ── Like + stats bar ────────────────────────────── */}
        <div style={{ margin:'52px 0 32px', padding:'24px', background: theme==='dark'?'var(--surface)':'rgba(0,0,0,0.04)', borderRadius:'var(--radius-lg)', border:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.08)'}`, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
          <button onClick={toggleLike} disabled={likePending} style={{
            display:'flex', alignItems:'center', gap:10, padding:'12px 24px',
            border:`2px solid ${liked?'var(--accent)':'var(--border2)'}`,
            borderRadius:40, background: liked?'rgba(229,49,112,0.12)':'transparent',
            cursor: user?'pointer':'default', transition:'all 0.25s var(--ease)',
            fontFamily:'var(--font-b)', fontWeight:700, fontSize:'0.9rem',
            color: liked ? 'var(--accent)' : T.text === '#d4d3e8' ? 'var(--text2)' : T.text,
          }}>
            <span style={{ fontSize:'1.3rem', transition:'transform 0.2s', transform: liked?'scale(1.25)':'scale(1)' }}>{liked?'♥':'♡'}</span>
            <span>{liked ? 'Liked' : 'Like this chapter'}</span>
            {likeCount > 0 && <span style={{ padding:'2px 8px', background:'rgba(255,255,255,0.08)', borderRadius:20, fontSize:'0.8rem' }}>{likeCount}</span>}
          </button>
          {!user && <span style={{ color:T.text==='#d4d3e8'?'var(--text3)':'rgba(0,0,0,0.4)', fontSize:'0.82rem' }}>
            <Link to="/login" style={{ color:'var(--gold)', fontWeight:700 }}>Login</Link> to like & comment
          </span>}
          <div style={{ marginLeft:'auto', display:'flex', gap:16, color:T.text==='#d4d3e8'?'var(--text3)':'rgba(0,0,0,0.5)', fontSize:'0.82rem' }}>
            <span>👁 {chapter.readCount?.toLocaleString()||0} reads</span>
            <span>💬 {commTotal} comments</span>
          </div>
        </div>

        {/* ── Prev / Next navigation ──────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:48 }}>
          {/* Prev */}
          {chapter.prev ? (
            <Link to={chapter.prev.hasAccess ? `/books/${bookId}/chapters/${chapter.prev._id}` : `/books/${bookId}`}
              style={{ display:'flex', flexDirection:'column', gap:6, padding:'18px 20px', background:theme==='dark'?'var(--surface)':'rgba(0,0,0,0.04)', border:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.08)'}`, borderRadius:'var(--radius-lg)', textDecoration:'none', transition:'border-color 0.2s, background 0.2s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--gold)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=theme==='dark'?'var(--border)':'rgba(0,0,0,0.08)';}}
            >
              <span style={{ color:'var(--text3)', fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>← Previous</span>
              <span style={{ fontFamily:'var(--font-d)', fontSize:'1rem', color:T.text, fontWeight:600 }}>
                Ch.{chapter.prev.chapterNumber} — {chapter.prev.title}
              </span>
              {!chapter.prev.hasAccess && <span className="badge badge-locked" style={{width:'fit-content',marginTop:4}}>🔒 Locked</span>}
            </Link>
          ) : <div />}

          {/* Next */}
          {chapter.next ? (
            <Link to={chapter.next.hasAccess ? `/books/${bookId}/chapters/${chapter.next._id}` : `/books/${bookId}`}
              style={{ display:'flex', flexDirection:'column', gap:6, padding:'18px 20px', background:theme==='dark'?'var(--surface)':'rgba(0,0,0,0.04)', border:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.08)'}`, borderRadius:'var(--radius-lg)', textDecoration:'none', transition:'border-color 0.2s', textAlign:'right' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--gold)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=theme==='dark'?'var(--border)':'rgba(0,0,0,0.08)';}}
            >
              <span style={{ color:'var(--text3)', fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>Next →</span>
              <span style={{ fontFamily:'var(--font-d)', fontSize:'1rem', color:T.text, fontWeight:600 }}>
                Ch.{chapter.next.chapterNumber} — {chapter.next.title}
              </span>
              {!chapter.next.hasAccess && <span className="badge badge-locked" style={{width:'fit-content',marginLeft:'auto',marginTop:4}}>🔒 Locked — <Link to={`/books/${bookId}`} style={{color:'var(--gold)'}}>Unlock</Link></span>}
            </Link>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6, padding:'18px 20px', background:theme==='dark'?'var(--surface)':'rgba(0,0,0,0.04)', border:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.08)'}`, borderRadius:'var(--radius-lg)', textAlign:'right', justifyContent:'center' }}>
              <span style={{ color:'var(--text3)', fontSize:'0.78rem' }}>✓ End of available chapters</span>
              <Link to={`/books/${bookId}`} style={{ color:'var(--gold)', fontSize:'0.85rem', fontWeight:700 }}>Back to book →</Link>
            </div>
          )}
        </div>

        {/* ── Comments section ─────────────────────────────── */}
        <div style={{ borderTop:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.1)'}`, paddingTop:40 }}>
          <button
            onClick={()=>setShowComments(s=>!s)}
            style={{ display:'flex', alignItems:'center', gap:12, background:'none', border:'none', cursor:'pointer', marginBottom:28, fontFamily:'var(--font-b)' }}
          >
            <span style={{ fontFamily:'var(--font-d)', fontSize:'1.6rem', fontWeight:600, color:T.text }}>
              Comments
            </span>
            <span style={{ padding:'4px 12px', background:'rgba(232,197,71,0.15)', color:'var(--gold)', borderRadius:20, fontSize:'0.82rem', fontWeight:700 }}>{commTotal}</span>
            <span style={{ color:'var(--text3)', fontSize:'0.85rem', marginLeft:4 }}>{showComments ? '▲ Hide' : '▼ Show'}</span>
          </button>

          {showComments && (
            <div>
              {/* New comment input */}
              {user ? (
                <div style={{ display:'flex', gap:12, marginBottom:32, alignItems:'flex-start' }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,var(--gold),var(--amber))', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'0.85rem', flexShrink:0, color:'#0f0e17' }}>
                    {user.name[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, display:'flex', flexDirection:'column', gap:8 }}>
                    <textarea
                      value={newComment}
                      onChange={e=>setNewComment(e.target.value)}
                      placeholder={`Share your thoughts on Chapter ${chapter.chapterNumber}…`}
                      rows={3}
                      style={{ width:'100%', padding:'12px 16px', background:theme==='dark'?'var(--bg3)':'rgba(0,0,0,0.05)', border:`1.5px solid ${theme==='dark'?'var(--border2)':'rgba(0,0,0,0.12)'}`, borderRadius:10, color:T.text, fontFamily:'var(--font-b)', fontSize:'0.9rem', resize:'vertical', outline:'none', transition:'border-color 0.2s' }}
                      onFocus={e=>{e.target.style.borderColor='var(--gold)';}}
                      onBlur={e=>{e.target.style.borderColor=theme==='dark'?'var(--border2)':'rgba(0,0,0,0.12)';}}
                    />
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ color:'var(--text3)', fontSize:'0.78rem' }}>{newComment.length}/2000</span>
                      <button className="btn btn-gold btn-sm" onClick={submitComment} disabled={posting || !newComment.trim() || newComment.length>2000}>
                        {posting ? 'Posting…' : 'Post Comment'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p style={{ color:'var(--text3)', marginBottom:24, fontSize:'0.9rem' }}>
                  <Link to="/login" style={{ color:'var(--gold)', fontWeight:700 }}>Login</Link> to join the discussion.
                </p>
              )}

              {/* Comment list */}
              {commLoading ? <div className="spinner" /> : (
                <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                  {comments.length === 0 ? (
                    <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text3)' }}>
                      <p style={{ fontSize:'2rem', marginBottom:8 }}>💬</p>
                      <p>No comments yet. Be the first!</p>
                    </div>
                  ) : (
                    comments.map(c => (
                      <div key={c._id} style={{ paddingBottom:18, borderBottom:`1px solid ${theme==='dark'?'var(--border)':'rgba(0,0,0,0.06)'}`, marginBottom:18 }}>
                        <CommentItem
                          comment={c} bookId={bookId} chapterId={chapterId}
                          currentUser={user} onDelete={deleteComment}
                          onLike={() => {}} onReplyPosted={handleReplyPosted}
                        />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
